import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Menu {
  id: number;
  title: string;
  restaurant?: {
    name: string;
  };
}

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'restaurant_owner' | 'agency' | 'user';
  restaurantId?: number;
  restaurant?: {
    id: number;
    name: string;
  };
  isActive: boolean;
  createdAt: string;
}

const EditUser: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'restaurant_owner',
    menuIds: [] as string[],
    isActive: true,
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loadingMenus, setLoadingMenus] = useState(false);

  // Fetch available menus
  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoadingMenus(true);
    try {
      const token = localStorage.getItem('token');
      // Get all menus without pagination
      const response = await axios.get('/menu/all', {
        params: { limit: 1000 }, // Get up to 1000 menus
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Extract menus from the response
        const menusData = response.data.data;
        if (menusData.menus) {
          setMenus(menusData.menus);
        } else {
          setMenus(menusData || []);
        }
      } else {
        toast.error('Failed to load menus');
      }
    } catch (error: any) {
      console.error('Error fetching menus:', error);
      toast.error('Failed to load menus');
    } finally {
      setLoadingMenus(false);
    }
  };

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          const userData = response.data.data;
          setUser(userData);
          setForm({
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || 'restaurant_owner',
            menuIds: [], // We don't fetch assigned menus from the API currently, so start empty
            isActive: userData.isActive,
            password: '',
            confirmPassword: ''
          });
        }
      } catch (error: any) {
        console.error('Failed to fetch user:', error);
        toast.error(error.response?.data?.error || t('edit_user.messages.load_error'));
        navigate('/users');
      } finally {
        setLoadingUser(false);
      }
    };

    if (id) {
      fetchUser();
    }
  }, [id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.email) {
      toast.error(t('edit_user.messages.name_email_required'));
      return;
    }

    if (form.password && form.password !== form.confirmPassword) {
      toast.error(t('edit_user.messages.passwords_dont_match'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const updateData: any = {
        name: form.name,
        email: form.email,
        role: form.role,
        restaurantId: null,
        isActive: form.isActive
      };

      // Only include password if it's provided
      if (form.password) {
        updateData.password = form.password;
      }

      // Update the user first
      await axios.put(
        `/users/${id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Assign menus to the updated user
      if (form.menuIds && form.menuIds.length > 0 && id) {
        for (const menuId of form.menuIds) {
          try {
            await axios.post(
              '/users/assign-menu',
              {
                userId: parseInt(id, 10),
                menuId: parseInt(menuId, 10)
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
          } catch (err) {
            console.error(`Failed to assign menu ${menuId}:`, err);
          }
        }
      }

      toast.success(t('edit_user.messages.update_success'));
      navigate('/users');
    } catch (error: any) {
      console.error('Update user error:', error);
      toast.error(error.response?.data?.error || t('edit_user.messages.update_error'));
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800">{t('edit_user.messages.user_not_found')}</p>
          <Link
            to="/users"
            className="mt-4 inline-flex items-center gap-2 text-[#0A0C0B] hover:text-[#7BD5B5]"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('edit_user.back_to_users')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/users"
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-[#0A0C0B]">{t('edit_user.title')}</h1>
          <p className="text-[#687d76] mt-1">{t('edit_user.subtitle')}</p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-xl p-6 space-y-5"
      >
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.name')}</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
            placeholder={t('edit_user.form.name_placeholder')}
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.email')}</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
            placeholder={t('edit_user.form.email_placeholder')}
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.new_password')}</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
              placeholder={t('edit_user.form.password_placeholder')}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#687d76]"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="text-xs text-[#687d76] mt-1">
            {t('edit_user.form.password_placeholder')}
          </p>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.confirm_password')}</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
              placeholder={t('edit_user.form.confirm_password_placeholder')}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#687d76]"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.role')}</label>
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5]"
          >
            <option value="admin">{t('user_management.roles.admin')}</option>
            <option value="agency">{t('user_management.roles.agency')}</option>
            <option value="restaurant_owner">{t('user_management.roles.restaurant_owner')}</option>
            <option value="editor">{t('user_management.roles.editor')}</option>
            <option value="user">{t('user_management.roles.user')}</option>
          </select>
        </div>

        {/* Assign Menus (optional) */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('create_user.form.assign_menus') || 'Assign Menus'}
          </label>
          <select
            multiple
            name="menuIds"
            value={form.menuIds}
            onChange={(e) => {
              const options = e.target.options;
              const value = [];
              for (let i = 0, l = options.length; i < l; i++) {
                if (options[i].selected) {
                  value.push(options[i].value);
                }
              }
              setForm({ ...form, menuIds: value });
            }}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#7BD5B5] min-h-[120px]"
          >
            {menus.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.id} - {menu.title}{menu.restaurant?.name ? ` (${menu.restaurant.name})` : ''}
              </option>
            ))}
          </select>
          {loadingMenus && (
            <div className="mt-2 flex items-center text-sm text-[#687d76]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('create_user.form.loading_menus') || 'Loading menus...'}
            </div>
          )}
          <p className="text-xs text-[#687d76] mt-1">
            {t('create_user.form.assign_menus_help') || 'Hold Ctrl/Cmd to select multiple menus.'}
          </p>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('edit_user.form.status')}</label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="isActive"
                checked={form.isActive}
                onChange={() => setForm({ ...form, isActive: true })}
                className="h-4 w-4 text-[#0A0C0B] focus:ring-[#7BD5B5]"
              />
              <span>{t('edit_user.form.active')}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="isActive"
                checked={!form.isActive}
                onChange={() => setForm({ ...form, isActive: false })}
                className="h-4 w-4 text-[#0A0C0B] focus:ring-[#7BD5B5]"
              />
              <span>{t('edit_user.form.inactive')}</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Link
            to="/users"
            className="px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-100"
          >
            {t('edit_user.buttons.cancel')}
          </Link>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#0A0C0B] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('edit_user.buttons.update_user')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;